import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./Billing.scss";
import { useHistory } from "react-router-dom";
import {
  FiChevronDown,
  FiMoreVertical,
  FiCalendar,
  FiX,
  FiFilter,
  FiSliders,
  FiCheck,
  FiXCircle,
} from "react-icons/fi";
import { AiOutlineSearch } from "react-icons/ai";
import { BiSort, BiFilterAlt, BiSortAlt2, BiCheck } from "react-icons/bi";
import CircularProgress from "@material-ui/core/CircularProgress";
import Popover from "@material-ui/core/Popover";
import config from "../../config/config";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import firebase from "../../config/firebase";
import { UserContext } from "../../Context/UserContext";
import { toast } from "react-toast";
import drawerTemplate from "../../hoc/drawerTemplate";
import ProjectService from "../../api/projectService";
import OrdersBillingAPI from "../../api/ordersBillingAPI";
import ProgressIndicator from "../../components/ProgressIndicator/ProgressIndicator";
import { DropDownMUI } from "../../components";
import NMRModal from "./NMRModal";

// Define delay constants at the top of the file
const SEARCH_DEBOUNCE_DELAY = 300;

// Create style element and append styles
const styleElement = document.createElement("style");
document.head.appendChild(styleElement);

const Billing = () => {
  const userContext = useContext(UserContext);
  const history = useHistory();

  // Enhanced cache for project data to avoid refetching
  const projectDataCache = useRef({
    // Store project data with timestamps for cache invalidation
    projects: {},
    // Track when cache was last refreshed
    lastRefreshed: Date.now(),
  });

  // Project states
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const projectService = new ProjectService();

  // Ref to track if initial fetch has happened
  const initialFetchRef = useRef(false);
  // Ref to preserve selected project between renders
  const selectedProjectIdRef = useRef(null);

  // Data states
  const [isSpinner, setSpinner] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [releasedOrders, setReleasedOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [loadedBills, setLoadedBills] = useState([]); // For incremental loading
  const [project, setProject] = useState({});

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeBillType, setActiveBillType] = useState(null);
  const [categoriesDropdown, setCategoriesDropdown] = useState(false);
  const [sortDropdown, setSortDropdown] = useState(false);
  const [filtersDropdown, setFiltersDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortOption, setSortOption] = useState("newest");
  const [selectedFilters, setSelectedFilters] = useState({
    status: [],
    hasBills: null,
  });

  // UI states
  const [selectedItems, setSelectedItems] = useState({});
  const [isLoadingIncremental, setIsLoadingIncremental] = useState(false);

  // Refs for dropdown closing
  const categoriesRef = useRef(null);
  const sortRef = useRef(null);
  const filtersRef = useRef(null);
  const projectSelectorRef = useRef(null);

  // New date picker states
  const [startDateAnchor, setStartDateAnchor] = useState(null);
  const [endDateAnchor, setEndDateAnchor] = useState(null);
  const [startDateObj, setStartDateObj] = useState(null);
  const [endDateObj, setEndDateObj] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showNMRModal, setShowNMRModal] = useState(false);
  const [activeTab, setActiveTab] = useState("regular"); // 'regular' or 'nmr'

  const handleGenerateClick = () => {
    console.log("Generate button clicked");
    setLoading(true);
    setTimeout(() => {
      history.push("/RaiseBill", {
        data: {
          ProjectId: selectedProject.ProjectId,

          ProjectName: selectedProject.ClientName,
          releasedOrders,
        },
      });
    }, 100); // slight delay for UI to update
  };

  // Initialize component
  useEffect(() => {
    // Load saved project ID early to avoid re-rendering
    const savedProjectId = localStorage.getItem("selectedProjectId");
    if (savedProjectId) {
      selectedProjectIdRef.current = savedProjectId;
    }

    // Use cache if it's less than 5 minutes old
    const cachedProjects = projectDataCache.current.projects;
    const cacheAge = Date.now() - projectDataCache.current.lastRefreshed;
    const isCacheValid = cacheAge < 300000; // 5 minutes

    if (isCacheValid && Object.keys(cachedProjects).length > 0) {
      console.log("Using cached project data");
      setProjects(Object.values(cachedProjects));

      // Select project from cache immediately for faster load
      if (savedProjectId && cachedProjects[savedProjectId]) {
        setSelectedProject(cachedProjects[savedProjectId]);
      }
    }

    // Always fetch fresh data, but don't block UI
    if (
      userContext.user?.accountType !== "Admin" &&
      userContext.user?.accountType !== "Owner"
    ) {
      getProjectsByUser();
    } else {
      getProjects();
    }

    // Add click event listener to close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      // Don't close dropdowns when clicking on the project selector
      if (
        projectSelectorRef.current &&
        projectSelectorRef.current.contains(event.target)
      ) {
        return;
      }

      if (
        categoriesRef.current &&
        !categoriesRef.current.contains(event.target) &&
        categoriesDropdown
      ) {
        console.log("Closing categories dropdown");
        setCategoriesDropdown(false);
      }

      if (
        sortRef.current &&
        !sortRef.current.contains(event.target) &&
        sortDropdown
      ) {
        console.log("Closing sort dropdown");
        setSortDropdown(false);
      }

      if (
        filtersRef.current &&
        !filtersRef.current.contains(event.target) &&
        filtersDropdown
      ) {
        console.log("Closing filters dropdown");
        setFiltersDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [categoriesDropdown, sortDropdown, filtersDropdown]);

  // Apply debouncing to search query
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Format date function
  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Calculate bill amount from workOrderItems and additionalItems
  const calculateBillAmount = (bill) => {
    let totalAmount = 0;

    // Calculate from workOrderItems
    if (bill.workOrderItems) {
      Object.values(bill.workOrderItems).forEach((item) => {
        if (item.items) {
          Object.values(item.items).forEach((lineItem) => {
            // Add line item amount
            totalAmount += parseFloat(lineItem.amount || 0);

            // Subtract deductions if any
            if (lineItem.deductions) {
              Object.values(lineItem.deductions).forEach((deduction) => {
                totalAmount -= parseFloat(deduction.amount || 0);
              });
            }
          });
        }
      });
    }

    // Calculate from additionalItems
    if (bill.additionalItems) {
      Object.values(bill.additionalItems).forEach((item) => {
        if (item.items) {
          Object.values(item.items).forEach((lineItem) => {
            // Add line item amount
            totalAmount += parseFloat(lineItem.amount || 0);

            // Subtract deductions if any
            if (lineItem.deductions) {
              Object.values(lineItem.deductions).forEach((deduction) => {
                totalAmount -= parseFloat(deduction.amount || 0);
              });
            }
          });
        }
      });
    }

    return totalAmount;
  };

  // Determine work type (Material/Work) from order items
  const determineBillType = (orderItems) => {
    if (!orderItems || orderItems.length === 0) return "Material Bill"; // Default to Material Bill if no items

    // Check for work type in items
    const workItems = orderItems.filter(
      (item) => item.workType === "Only Work"
    );
    const materialItems = orderItems.filter(
      (item) => item.workType !== "Only Work" || !item.workType
    );

    // Determine bill type based on combination of items
    if (workItems.length > 0 && materialItems.length > 0) {
      return "Work + Material";
    } else if (workItems.length > 0) {
      return "Work Bill";
    } else {
      return "Material Bill";
    }
  };

  useEffect(() => {
    if (selectedProject) {
      let projectId = selectedProject.ProjectId;
      firebase
        .database()
        .ref("liveBOQ/" + projectId.toString())
        .on("value", (snapshot) => {
          if (snapshot.exists()) {
            console.log(
              "typeof snapshot.val().rooms one : ",
              typeof snapshot.val().rooms
            );
            const response = {
              ...snapshot.val(),
              rooms: JSON.parse(snapshot.val().rooms),
              Rooms: JSON.parse(snapshot.val().rooms),
            }; // has to replace with rooms or Rooms
            console.log("typeof response one : ", typeof response.rooms);
            setProject(response);
            // setSelectedProject(response);
          }
        });
    }
  }, [selectedProject]);
  // Get all projects with caching
  const getProjects = () => {
    // Only show spinner if we don't have cached data
    if (Object.keys(projectDataCache.current.projects).length === 0) {
      setSpinner(true);
    }

    projectService
      .getAllProjects()
      .then((res) => {
        if (res.status === 200) {
          let activeProjects = res.payload.filter(
            (project) =>
              (!project.isArchived || project.isArchived === 0) &&
              project.ClientName &&
              project.ClientName.trim() !== ""
          );

          // Sort projects alphabetically by ClientName
          activeProjects.sort((a, b) =>
            (a.ClientName || "").localeCompare(b.ClientName || "")
          );

          // Cache projects by ID for faster lookup
          const projectsById = {};
          activeProjects.forEach((project) => {
            projectsById[project.ProjectId] = project;
          });

          // Update cache with timestamp
          projectDataCache.current.projects = projectsById;
          projectDataCache.current.lastRefreshed = Date.now();

          setProjects(activeProjects);

          // Only select a project if we don't have one already
          if (activeProjects.length > 0 && !selectedProject) {
            // Get saved project ID from localStorage first, then fall back to ref
            const savedProjectId =
              localStorage.getItem("selectedProjectId") ||
              selectedProjectIdRef.current;

            if (savedProjectId) {
              const savedProject = activeProjects.find(
                (p) => p.ProjectId.toString() === savedProjectId.toString()
              );
              if (savedProject) {
                setSelectedProject(savedProject);
                return; // Exit early if we found a saved project
              }
            }

            // No saved project found, find a project with data
            findProjectWithData(activeProjects);
          }
        }
        setSpinner(false);
      })
      .catch((e) => {
        console.error("Error fetching projects:", e);
        toast.error("Error fetching projects!");
        setSpinner(false);
      });
  };

  // Get projects by user (for non-admin users) with enhanced caching
  const getProjectsByUser = () => {
    // Only show spinner if we don't have cached data
    if (Object.keys(projectDataCache.current.projects).length === 0) {
      setSpinner(true);
    }

    try {
      // Parse assigned projects once and memoize the result
      const assignedProjects = JSON.parse(
        userContext.user?.AssignedProjects || "[]"
      );

      if (!assignedProjects.length) {
        setSpinner(false);
        return;
      }

      // Create a Set of project IDs for faster lookup later
      const assignedProjectIdSet = new Set(
        assignedProjects.map((p) => p.ProjectId.toString())
      );

      // Format for API call
      let projectIdstobefetched = assignedProjects
        .map((p) => `'${p.ProjectId}'`)
        .join(",");

      projectService
        .getAllProjectsByUser(projectIdstobefetched)
        .then((res) => {
          if (res.status === 200) {
            let activeProjects = res.payload.filter(
              (project) =>
                (!project.isArchived || project.isArchived === 0) &&
                project.ClientName &&
                project.ClientName.trim() !== ""
            );

            // Sort projects alphabetically by ClientName
            activeProjects.sort((a, b) =>
              (a.ClientName || "").localeCompare(b.ClientName || "")
            );

            // Cache projects by ID for faster lookup
            const projectsById = {};
            activeProjects.forEach((project) => {
              projectsById[project.ProjectId] = project;
            });

            // Update cache with timestamp
            projectDataCache.current.projects = projectsById;
            projectDataCache.current.lastRefreshed = Date.now();

            setProjects(activeProjects);

            // Only select a project if we don't have one already
            if (activeProjects.length > 0 && !selectedProject) {
              // Get saved project ID from localStorage first, then fall back to ref
              const savedProjectId =
                localStorage.getItem("selectedProjectId") ||
                selectedProjectIdRef.current;

              if (
                savedProjectId &&
                assignedProjectIdSet.has(savedProjectId.toString())
              ) {
                const savedProject = activeProjects.find(
                  (p) => p.ProjectId.toString() === savedProjectId.toString()
                );
                if (savedProject) {
                  setSelectedProject(savedProject);
                  return; // Exit early if we found a saved project
                } else {
                  console.log(
                    "Saved project not found in user's active projects"
                  );
                }
              }

              // No saved project found, find a project with data
              findProjectWithData(activeProjects);
            }
          }
          setSpinner(false);
        })
        .catch((e) => {
          console.error("Error fetching user projects:", e);
          toast.error("Error fetching projects!");
          setSpinner(false);
        });
    } catch (error) {
      console.error("Error parsing assigned projects:", error);
      setSpinner(false);
    }
  };

  // Optimized function to find a project with data and set it as selected
  const findProjectWithData = async (projects) => {
    setLoadingMessage("Finding a project with data...");

    try {
      // Check for saved projectId first - if it exists in localStorage, prioritize it
      const savedProjectId = localStorage.getItem("selectedProjectId");
      if (savedProjectId) {
        const savedProject = projects.find(
          (p) => p.ProjectId.toString() === savedProjectId.toString()
        );

        if (savedProject) {
          console.log("Using saved project from localStorage");
          setSelectedProject(savedProject);
          selectedProjectIdRef.current = savedProject.ProjectId;
          setLoadingMessage("");
          setLoadingProgress(0);
          return;
        }
      }

      // Check for last opened project - many applications store this
      const lastOpenedProjects = projects
        .filter((p) => p.lastOpen)
        .sort((a, b) => parseInt(b.lastOpen) - parseInt(a.lastOpen));

      if (lastOpenedProjects.length > 0) {
        // Use the most recently opened project
        const mostRecentProject = lastOpenedProjects[0];
        setSelectedProject(mostRecentProject);
        selectedProjectIdRef.current = mostRecentProject.ProjectId;
        setLoadingMessage("");
        setLoadingProgress(0);
        return;
      }

      // If no previously saved/opened project, check data more efficiently
      // Check multiple projects concurrently but limit to just first 5
      const projectsToCheck = projects.slice(0, 5);

      // Create promises for all project checks but don't await them yet
      const checkPromises = projectsToCheck.map((project) => {
        // Check extended cache first
        if (projectDataCache.current.projects?.[project.ProjectId]?.orderData) {
          const cachedResult =
            projectDataCache.current.projects[project.ProjectId].orderData;
          return Promise.resolve({
            project,
            hasData: cachedResult.hasData,
            dataCount: cachedResult.dataCount,
          });
        }

        return new Promise(async (resolve) => {
          try {
            // Use a shorter timeout and abort controller for faster response
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 300); // Even shorter timeout

            const response = await OrdersBillingAPI.GetReleasedOrders(
              project.ProjectId
            );
            clearTimeout(timeoutId);

            // Cache the result in the enhanced cache structure
            const result = {
              hasData:
                !response.isError && response.data && response.data.length > 0,
              dataCount: response.data ? response.data.length : 0,
              data: response.data || [],
            };

            // Store in the enhanced cache structure
            if (!projectDataCache.current.projects[project.ProjectId]) {
              projectDataCache.current.projects[project.ProjectId] = {};
            }
            projectDataCache.current.projects[project.ProjectId].orderData =
              result;

            resolve({
              project,
              ...result,
            });
          } catch (error) {
            resolve({ project, hasData: false, dataCount: 0 });
          }
        });
      });

      // Create a race between promises - select first project with data
      // This is faster than waiting for all promises to resolve
      let foundProject = false;

      for (const promise of checkPromises) {
        const result = await promise;
        if (result.hasData) {
          setSelectedProject(result.project);
          selectedProjectIdRef.current = result.project.ProjectId;
          foundProject = true;
          break;
        }
      }

      // If no project with data was found, default to first project
      if (!foundProject) {
        setSelectedProject(projects[0]);
        selectedProjectIdRef.current = projects[0].ProjectId;
      }
    } catch (error) {
      console.error("Error finding project with data:", error);
      // Fallback to first project
      if (projects.length > 0) {
        setSelectedProject(projects[0]);
        selectedProjectIdRef.current = projects[0].ProjectId;
      }
    }

    setLoadingMessage("");
    setLoadingProgress(0);
  };

  // Update when project selection changes
  useEffect(() => {
    if (selectedProject) {
      // Store the selected project ID in localStorage
      localStorage.setItem("selectedProjectId", selectedProject.ProjectId);

      // Store the selected project ID in the ref
      selectedProjectIdRef.current = selectedProject.ProjectId;

      // Reset filters when project selection changes
      setSearchQuery("");
      setActiveBillType(null);

      // Only fetch if we haven't fetched yet or it's a new project selection
      if (
        !initialFetchRef.current ||
        initialFetchRef.current !== selectedProject.ProjectId
      ) {
        initialFetchRef.current = selectedProject.ProjectId;
        fetchReleasedOrders(selectedProject);
      }
    }
  }, [selectedProject]);

  // Handle project selection
  const handleProjectSelection = (selectedProjectId) => {
    // Close any open dropdowns first
    setCategoriesDropdown(false);
    setSortDropdown(false);
    setFiltersDropdown(false);

    if (!selectedProjectId) return;

    const project = projects.find(
      (p) => p.ProjectId.toString() === selectedProjectId.toString()
    );

    if (project) {
      setSearchQuery("");
      setActiveBillType(null);
      setBills([]);
      // Clear the fetch ref to force a new fetch
      initialFetchRef.current = false;
      // Update the selected project ref
      selectedProjectIdRef.current = project.ProjectId;
      // Save to localStorage
      localStorage.setItem("selectedProjectId", project.ProjectId);
      setSelectedProject(project);
    } else {
      toast.error("Project not found");
    }
  };

  // Optimized fetch released orders for the selected project
  const fetchReleasedOrders = async (project) => {
    try {
      // Don't show central spinner, only use progress indicator
      setIsLoadingIncremental(true);
      setLoadingMessage("Fetching purchase orders...");
      setLoadingProgress(10);

      // Don't clear bills immediately to prevent UI flash
      // Instead, use a delayed clear if no cache hit
      const clearTimeoutId = setTimeout(() => {
        setBills([]);
        setLoadedBills([]);
      }, 200);

      // Check if we have cached data in the enhanced cache structure
      if (
        projectDataCache.current.projects?.[project.ProjectId]?.orderData?.data
      ) {
        clearTimeout(clearTimeoutId);
        const cachedData =
          projectDataCache.current.projects[project.ProjectId].orderData.data;

        console.log(
          `Using cached data for project ${project.ClientName} (${cachedData.length} orders)`
        );
        setReleasedOrders(cachedData);
        setLoadingProgress(30);
        setLoadingMessage("Processing purchase orders...");

        // Check if we also have processed bills cached
        if (
          projectDataCache.current.projects[project.ProjectId].processedBills
        ) {
          const cachedBills =
            projectDataCache.current.projects[project.ProjectId].processedBills;
          console.log(`Using ${cachedBills.length} cached processed bills`);

          // Set bills from cache immediately
          setBills(cachedBills);
          setLoadedBills(cachedBills);

          // Still process in background but with lower priority
          setTimeout(() => {
            fetchBillingDataIncremental(cachedData, project, true);
          }, 100);

          // Update UI immediately
          setIsLoadingIncremental(false);
          setLoadingMessage("");
          setLoadingProgress(100);
          return;
        }

        // No processed bills cache, process orders incrementally
        await fetchBillingDataIncremental(cachedData, project);
        return;
      }

      // No cache hit, fetch fresh data
      const response = await OrdersBillingAPI.GetReleasedOrders(
        project.ProjectId
      );

      if (response.isError) {
        clearTimeout(clearTimeoutId);
        throw new Error(response.errorMessage);
      }

      // Cache the data in the enhanced structure
      if (!projectDataCache.current.projects[project.ProjectId]) {
        projectDataCache.current.projects[project.ProjectId] = {};
      }

      projectDataCache.current.projects[project.ProjectId].orderData = {
        hasData: response.data && response.data.length > 0,
        dataCount: response.data ? response.data.length : 0,
        data: response.data || [],
        timestamp: Date.now(),
      };

      setReleasedOrders(response.data || []);
      setLoadingProgress(30);
      setLoadingMessage("Processing purchase orders...");
      clearTimeout(clearTimeoutId);

      // Process orders incrementally
      await fetchBillingDataIncremental(response.data || [], project);
    } catch (error) {
      console.error("Error fetching released orders:", error);
      toast.error("Error fetching released orders!");
      setIsLoadingIncremental(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    }
  };

  // Parse order data to get line items
  const parseOrderData = (orderDataString) => {
    try {
      const data = JSON.parse(orderDataString || "{}");
      return data.data || [];
    } catch (e) {
      console.error("Error parsing order data:", e);
      return [];
    }
  };

  // Optimized process for a single purchase order with improved JSON parsing
  const processSingleOrder = async (
    order,
    project,
    billsByPO,
    index,
    total
  ) => {
    try {
      // Update loading progress less frequently - only for every 10th item for better performance
      if (index % 10 === 0) {
        setLoadingProgress(30 + Math.floor(((index + 0.3) / total) * 70));
        setLoadingMessage(`Processing PO #${order.Id} (${index + 1}/${total})`);
      }

      // Check if we have pre-processed data cached for this order
      const projectCache = projectDataCache.current.projects[project.ProjectId];
      const orderCacheKey = `order_${order.Id}`;

      if (projectCache && projectCache[orderCacheKey]) {
        // Use cached processed order data
        return projectCache[orderCacheKey];
      }

      // Parse order data and items with better error handling
      let orderItems = [];
      // Use the parseOrderData function
      try {
        orderItems = parseOrderData(order.Data);
      } catch (e) {
        console.error(`Error parsing order items for PO #${order.Id}:`, e);
      }

      // Create a more efficient JSON parser with error handling
      const safeJsonParse = (jsonString, defaultValue = {}) => {
        if (!jsonString || typeof jsonString !== "string") return defaultValue;
        try {
          return JSON.parse(jsonString);
        } catch (e) {
          return defaultValue;
        }
      };

      // Parse all JSON data more efficiently using the optimized parser
      // This avoids unnecessary Promise overhead for simple parsing
      const vendorInfo = safeJsonParse(order.VendorId);
      const orderData = safeJsonParse(order.Data);
      const requestHistory = safeJsonParse(order.requestHistory);

      // Extract all needed data
      const clientName = orderData.ClientName || "Construction Project";
      const statusEntry = requestHistory[order.Id] || {};
      const category = Object.keys(vendorInfo)[0] || order.Category || "Civil";
      const vendorName =
        vendorInfo.vendorCompanyName || order.VendorName || "Vendor Name";
      const vendorFirm =
        order.VendorNameFromId || vendorInfo.vendorCompanyName || "Vendor Firm";

      // Determine bill type based on order items
      const billType = determineBillType(orderItems);

      // Create PO entry
      const poEntry = {
        billId: `po-${order.Id}`,
        poId: order.Id,
        serviceOrder: order,
        orderItems: orderItems.map((item) => ({
          ...item,
          selected: false,
          id: item.id || `item-${Math.random().toString(36).substr(2, 9)}`,
        })),
        date: statusEntry.date || formatDate(new Date(parseInt(order.Id))),
        time: statusEntry.time || "12:00 PM",
        status: order.Status || "Pending",
        billType,
        vendor: { name: vendorName, category, firm: vendorFirm },
        client: {
          name: clientName,
          location: order.Address || "Client Location",
        },
        billNo: order.Id.toString().slice(-8),
        remarks: `PO #${order.Id} - View line items and generate bills`,
        isPO: true,
        hasBills: false,
        raisedBy: statusEntry.By || "System",
        approvedBy: order.ApprovedBy || "Pending",
        isLoading: true,
        totalAmount: 0, // Will be calculated when billing info arrives
      };

      // Add PO to loaded bills immediately - use functional update
      setLoadedBills((prev) => [...prev, poEntry]);

      // Get billing information directly without throttling
      const billingResponse = await OrdersBillingAPI.GetBillingInformation(
        order.Id
      );
      const poData = billingResponse.data;

      const results = [];
      const updatedPoEntry = {
        ...poEntry,
        isLoading: false,
      };

      if (Object.keys(poData).length > 0) {
        billsByPO[order.Id] = true;
        updatedPoEntry.hasBills = true;

        // Calculate the total bill amount for this PO
        let poTotalAmount = 0;

        // Process all bills for this PO
        Object.keys(poData).forEach((timestamp) => {
          const bill = poData[timestamp];

          // Calculate bill amount from workOrderItems and additionalItems
          const billAmount = calculateBillAmount(bill);
          poTotalAmount += billAmount;

          // Create the bill object with calculated amount
          results.push({
            ...bill,
            poId: order.Id,
            timestamp,
            billId: timestamp,
            serviceOrder: order,
            orderItems: orderItems,
            vendor: bill.vendor || {
              name: vendorName,
              category,
              firm: vendorFirm,
            },
            client: {
              name: clientName,
              location: order.Address || "Client Location",
            },
            date: bill.date || formatDate(new Date(parseInt(timestamp))),
            time: bill.time || "12:00 PM",
            status: bill.status || "Pending",
            billType,
            billNo: bill.billNo || timestamp.toString().slice(-8),
            remarks: bill.remarks || `Bill for PO #${order.Id}`,
            raisedBy: bill.raisedBy || statusEntry.By || "System",
            approvedBy: bill.approvedBy || "Pending",
            totalAmount: billAmount, // Set calculated bill amount
            workOrderItems: bill.workOrderItems || {},
            additionalItems: bill.additionalItems || {},
          });
        });

        // Set total amount for PO
        updatedPoEntry.totalAmount = poTotalAmount;
      }

      results.push(updatedPoEntry);

      // Update loaded bills with functional update to prevent race conditions
      setLoadedBills((prev) => {
        const filteredPrev = prev.filter(
          (bill) => bill.billId !== `po-${order.Id}`
        );
        return [...filteredPrev, ...results];
      });

      return results;
    } catch (error) {
      console.error("Error processing order:", error);
      return [];
    }
  };

  // Fetch billing information incrementally
  const fetchBillingDataIncremental = async (orders = [], project) => {
    try {
      setIsLoadingIncremental(true);

      if (!project) {
        setIsLoadingIncremental(false);
        setLoadingMessage("");
        setLoadingProgress(0);
        return;
      }

      // Filter orders that match the project
      const projectOrders = orders.filter(
        (order) =>
          order.ProjectId &&
          order.ProjectId.toString() === project.ProjectId.toString()
      );

      if (projectOrders.length === 0) {
        setBills([]);
        setIsLoadingIncremental(false);
        return;
      }

      // Extract categories more efficiently with Set
      const categorySet = new Set();
      projectOrders.forEach((order) => {
        try {
          const vendorInfo = JSON.parse(order.VendorId || "{}");
          const category =
            Object.keys(vendorInfo)[0] || order.Category || "Civil";
          categorySet.add(category);
        } catch (e) {
          // Silent fail
        }
      });
      setCategories(Array.from(categorySet));

      // Process orders with larger batches
      const totalOrders = projectOrders.length;
      const billsByPO = {};
      const allBills = [];

      // Clear loaded bills before starting
      setLoadedBills([]);

      // Increase batch size for faster processing
      const batchSize = 20; // Increased for even faster processing
      for (let i = 0; i < projectOrders.length; i += batchSize) {
        const batch = projectOrders.slice(i, i + batchSize);

        // Process all orders in batch concurrently
        const promises = batch.map((order, batchIndex) =>
          processSingleOrder(
            order,
            project,
            billsByPO,
            i + batchIndex,
            totalOrders
          )
        );

        // Wait for all promises in batch to complete
        const batchResults = await Promise.all(promises);

        // Add to allBills
        batchResults.forEach((orderBills) => {
          if (orderBills && orderBills.length > 0) {
            allBills.push(...orderBills);
          }
        });

        // Update progress less frequently
        setLoadingProgress(30 + Math.floor((i / projectOrders.length) * 70));
      }

      // Set final bills
      setBills(allBills.reverse());

      // Complete loading immediately
      setLoadingProgress(100);
      setLoadingMessage("Loading complete");
      setIsLoadingIncremental(false);
      setLoadingMessage("");
      setLoadingProgress(0);
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast.error("Error loading purchase orders!");
      setIsLoadingIncremental(false);
    }
  };

  // Handle item selection to prevent crashing
  const handleItemSelection = (poId, itemIndex) => {
    // Update the primary bills state
    setBills((prevBills) => {
      return prevBills.map((bill) => {
        if (bill.poId === poId && bill.isPO) {
          const updatedOrderItems = [...bill.orderItems];
          updatedOrderItems[itemIndex] = {
            ...updatedOrderItems[itemIndex],
            selected: !updatedOrderItems[itemIndex].selected,
          };

          return {
            ...bill,
            orderItems: updatedOrderItems,
          };
        }
        return bill;
      });
    });

    // Also update the loadedBills state for incrementally loaded items
    setLoadedBills((prevLoadedBills) => {
      return prevLoadedBills.map((bill) => {
        if (bill.poId === poId && bill.isPO) {
          const updatedOrderItems = [...bill.orderItems];
          updatedOrderItems[itemIndex] = {
            ...updatedOrderItems[itemIndex],
            selected: !updatedOrderItems[itemIndex].selected,
          };

          return {
            ...bill,
            orderItems: updatedOrderItems,
          };
        }
        return bill;
      });
    });
  };
  // Async function to get bank account details
  const getBankAccountDetails = async (Mobile) => {
    const Token =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJJZCI6MiwiTmFtZSI6IlNoaXZhIiwiTW9iaWxlIjo4MDE5NzMxNzkwLCJTdGF0ZSI6IlRTIiwiQ2l0eSI6Ikh5ZGVyYWJhZCIsIkFyZWEiOiJIaXRlY2ggY2l0eSIsIlN0cmVldCI6IktQSEIgcGhhc2UgNyIsIkJ1bGRpbmciOiJJbmR1IEZvcnR1bmUiLCJGbGF0Tm8iOiIxMDIiLCJpYXQiOjE2MDk3Mzc0NjJ9.1B0hEQ9dTmwBLv5c0pFqnH9ES4sHr4IYFcIsLzFe9FQ";

    try {
      const response = await fetch(
        `${config.profileService}bankAccount/${Mobile}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      const data = await response.json();

      if (data.payload && data.payload.length > 0) {
        return data.payload[0];
      } else {
        console.warn("No bank details found for this mobile number");
        return null;
      }
    } catch (error) {
      console.error("Error fetching bank account details:", error);
      return null;
    }
  };

  // Async function to generate a new bill
  const [generatingBillId, setGeneratingBillId] = useState(null);

  const generateBill = async (serviceOrder) => {
    if (!serviceOrder) {
      toast.error("No service order selected!");
      return;
    }

    const {
      orderItems = [],
      isLoading,
      serviceOrder: so,
      vendor = {},
    } = serviceOrder;

    const selectedItems = orderItems.filter((item) => item.selected);

    if (selectedItems.length === 0) {
      toast.error(
        "Please select at least one line item before generating a bill"
      );
      return;
    }

    if (isLoading) {
      toast.error("Please wait for purchase order details to finish loading");
      return;
    }

    setGeneratingBillId(serviceOrder.Id); // <-- show loading on specific button

    const enhancedItems = selectedItems.map((item) => ({
      ...item,
      heading: item.heading || item.category || vendor.category || "Civil",
    }));

    let bankDetails = null;

    if (so?.VendorMobile) {
      bankDetails = await getBankAccountDetails(so.VendorMobile);
    }

    // Find all related bills for this PO
    const relatedBills = bills.filter(
      (b) => !b.isPO && b.poId === serviceOrder.poId
    );

    // Sort related bills chronologically (oldest to newest)
    const sortedRelatedBills = [...relatedBills].sort((a, b) => {
      const timeA = parseInt(a.billId);
      const timeB = parseInt(b.billId);

      // If both are valid timestamps, sort by time
      if (!isNaN(timeA) && !isNaN(timeB)) {
        return timeA - timeB;
      }
      // Otherwise sort by string comparison
      return String(a.billId).localeCompare(String(b.billId));
    });

    // The most recently created bill for reference
    const referenceBill =
      sortedRelatedBills.length > 0
        ? sortedRelatedBills[sortedRelatedBills.length - 1]
        : null;

    console.log(
      "Related bills:",
      sortedRelatedBills.length,
      "Latest bill:",
      referenceBill?.billId
    );

    history.push("/RaiseBill", {
      data: {
        ProjectId: selectedProject.ProjectId,
        ProjectName: selectedProject.ClientName,
        serviceOrder: {
          ...serviceOrder,
          selectedItems: enhancedItems,
        },
        vendor,
        client: {
          name: selectedProject.ClientName,
          location:
            serviceOrder.client?.location || selectedProject.Address || "",
        },
        poId: serviceOrder.poId,
        orderId: serviceOrder.Id || "",
        billType: serviceOrder.billType || "Work Bill",
        vendorName: so?.VendorName || "Vendor Name",
        vendorFirm:
          (so?.VendorId && JSON.parse(so.VendorId)?.vendorCompanyName) ||
          "Vendor Firm",
        vendorMobile: so?.VendorMobile || "",
        releasedOrders,
        bankAccountDetails: bankDetails || {},
        relatedBills: sortedRelatedBills, // Pass chronologically sorted bills
        currentBillIndex: sortedRelatedBills.length, // Index for the new bill
        referenceBillData: referenceBill, // Pass the most recent bill as reference for matching
      },
    });
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status) {
      case "Approved":
        return "status-approved";
      case "Checked":
        return "status-checked";
      case "Requested":
        return "status-raised";
      case "Pending":
        return "status-pending";
      case "Draft":
        return "status-draft";
      default:
        return "status-raised";
    }
  };

  // Handle bill type selection
  const handleBillTypeClick = (type) => {
    if (activeBillType === type) {
      setActiveBillType(null);
    } else {
      setActiveBillType(type);
    }
  };

  // Calculate item amount
  const calculateItemAmount = (item) => {
    const quantity = parseFloat(item.quantity || 0);
    const rate = parseFloat(item.rate || 0);
    const gst = parseFloat(item.gst || 0) / 100;

    const baseAmount = quantity * rate;
    const gstAmount = baseAmount * gst;

    return baseAmount + gstAmount;
  };

  // Format display date
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "N/A";

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return formatDate(date);
    } catch (e) {
      return dateString;
    }
  };

  // Format currency amount
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "0.00";

    return numAmount.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  // Toggle filter dropdowns
  const toggleCategoriesDropdown = (e) => {
    // Make sure we have access to the event
    if (e) e.stopPropagation();

    console.log("Toggling categories dropdown");
    setCategoriesDropdown((prev) => !prev);
    setSortDropdown(false);
    setFiltersDropdown(false);
  };

  const toggleSortDropdown = (e) => {
    // Make sure we have access to the event
    if (e) e.stopPropagation();

    console.log("Toggling sort dropdown");
    setSortDropdown((prev) => !prev);
    setCategoriesDropdown(false);
    setFiltersDropdown(false);
  };

  const toggleFiltersDropdown = (e) => {
    // Make sure we have access to the event
    if (e) e.stopPropagation();

    console.log("Toggling filters dropdown");
    setFiltersDropdown((prev) => !prev);
    setCategoriesDropdown(false);
    setSortDropdown(false);
  };

  // Handle category selection
  const toggleCategory = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((cat) => cat !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  // Handle sort option selection
  const handleSortSelection = (option) => {
    setSortOption(option);
    setSortDropdown(false);
  };

  // Handle filter selection
  const toggleFilter = (type, value) => {
    console.log(`Toggling filter ${type} with value ${value}`);
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };

      if (type === "status") {
        if (newFilters.status.includes(value)) {
          newFilters.status = newFilters.status.filter((s) => s !== value);
          console.log(`Removed status filter ${value}`);
        } else {
          newFilters.status = [...newFilters.status, value];
          console.log(`Added status filter ${value}`);
        }
      } else if (type === "hasBills") {
        if (newFilters.hasBills === value) {
          newFilters.hasBills = null;
        } else {
          newFilters.hasBills = value;
        }
      }

      console.log("New filters:", JSON.stringify(newFilters));
      return newFilters;
    });
  };

  // Date picker handlers
  const handleStartDateClick = (event) => {
    setStartDateAnchor(event.currentTarget);
  };

  const handleEndDateClick = (event) => {
    setEndDateAnchor(event.currentTarget);
  };

  const handleStartDateClose = () => {
    setStartDateAnchor(null);
  };

  const handleEndDateClose = () => {
    setEndDateAnchor(null);
  };

  const handleStartDateChange = (date) => {
    if (date && !isNaN(date.getTime())) {
      setStartDateObj(date);
      setStartDate(formatDate(date));
    } else {
      setStartDateObj(null);
      setStartDate("");
    }
  };

  const handleEndDateChange = (date) => {
    if (date && !isNaN(date.getTime())) {
      setEndDateObj(date);
      setEndDate(formatDate(date));
    } else {
      setEndDateObj(null);
      setEndDate("");
    }
  };

  const handleDateFilterApply = () => {
    if (startDateObj && endDateObj && startDateObj > endDateObj) {
      toast.error("Start date cannot be after end date");
      return;
    }

    handleStartDateClose();
    handleEndDateClose();

    // Filtering will happen through the filteredBills useMemo
  };

  // Clear date filters
  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStartDateObj(null);
    setEndDateObj(null);
    setStartDateAnchor(null);
    setEndDateAnchor(null);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSortOption("newest");
    setSelectedFilters({
      status: [],
      hasBills: null,
    });
    clearDateFilters();
  };

  // Filter and sort bills
  const filteredBills = useMemo(() => {
    // First, identify POs that have bills matching the status filter
    let posWithMatchingBills = {};

    if (selectedFilters.status.length > 0) {
      // Create a map of PO IDs to track which ones have matching bills
      bills.forEach((bill) => {
        if (!bill.isPO && bill.poId) {
          // Check if this bill matches the status filter
          const billStatus = (bill.status || "").toString().toLowerCase();
          const matchesStatusFilter = selectedFilters.status.some(
            (selectedStatus) => billStatus === selectedStatus.toLowerCase()
          );

          if (matchesStatusFilter) {
            // Mark this PO as having at least one matching bill
            posWithMatchingBills[bill.poId] = true;
          }
        }
      });

      console.log(
        "POs with matching status bills:",
        Object.keys(posWithMatchingBills)
      );
    }

    return bills
      .filter((bill) => {
        // Bill type filter - improved logic for more accurate filtering
        if (activeBillType) {
          if (bill.billType !== activeBillType) {
            return false;
          }
        }

        // Category filter
        if (selectedCategories.length > 0) {
          const category = bill.vendor?.category || "";
          if (!selectedCategories.includes(category)) {
            return false;
          }
        }

        // Status filter logic
        if (selectedFilters.status.length > 0) {
          // Special handling for PO entries - keep them if they have matching bills
          if (bill.isPO) {
            // If this PO has any bills that match the filter, keep it
            if (posWithMatchingBills[bill.poId]) {
              return true;
            }
          }

          // For bills, apply normal status filter
          const billStatus = (bill.status || "").toString().toLowerCase();
          const matchesStatus = selectedFilters.status.some(
            (selectedStatus) => billStatus === selectedStatus.toLowerCase()
          );

          if (!matchesStatus) {
            return false;
          }
        }

        // Has bills filter
        if (selectedFilters.hasBills !== null) {
          if (selectedFilters.hasBills && !bill.hasBills) {
            return false;
          }
          if (!selectedFilters.hasBills && bill.hasBills) {
            return false;
          }
        }

        // Date filter
        if (startDate || endDate) {
          // Get the PO entry for this bill
          const poEntry = bill.isPO
            ? bill
            : bills.find((b) => b.isPO && b.poId === bill.poId);

          if (!poEntry) return false;

          // Use the formatted display date that's shown in the UI
          const displayDate = formatDisplayDate(poEntry.date);
          if (displayDate === "N/A") return false;

          // Parse the formatted display date correctly
          const [day, month, year] = displayDate.split("-").map(Number);
          if (!day || !month || !year) return false;

          const poTimestamp = new Date(year, month - 1, day).getTime();

          if (startDate) {
            const [startDay, startMonth, startYear] = startDate
              .split("-")
              .map(Number);
            if (startDay && startMonth && startYear) {
              const startTimestamp = new Date(
                startYear,
                startMonth - 1,
                startDay
              ).getTime();
              if (poTimestamp < startTimestamp) return false;
            }
          }

          if (endDate) {
            const [endDay, endMonth, endYear] = endDate.split("-").map(Number);
            if (endDay && endMonth && endYear) {
              const endTimestamp = new Date(
                endYear,
                endMonth - 1,
                endDay
              ).getTime();
              if (poTimestamp > endTimestamp) return false;
            }
          }
        }

        // Search query
        if (debouncedSearchQuery) {
          const query = debouncedSearchQuery.toLowerCase().trim();

          // Check all searchable fields (expanded for more thorough searching)
          const vendorName = (bill.vendor?.name || "").toLowerCase();
          const vendorFirm = (bill.vendor?.firm || "").toLowerCase();
          const clientName = (bill.client?.name || "").toLowerCase();
          const billNo = bill.billNo || "";
          const billId = (bill.billId || "").toString().toLowerCase();
          const poId = (bill.poId || "").toString().toLowerCase();
          const category = (bill.vendor?.category || "").toLowerCase();
          const remarks = (bill.remarks || "").toLowerCase();
          const status = (bill.status || "").toLowerCase();

          // Search in description/specification of order items
          let matchesInItems = false;
          if (bill.orderItems && bill.orderItems.length > 0) {
            matchesInItems = bill.orderItems.some((item) => {
              const heading = (item.heading || "").toLowerCase();
              const spec = (item.specification || "").toLowerCase();
              const desc = (item.description || "").toLowerCase();
              return (
                heading.includes(query) ||
                spec.includes(query) ||
                desc.includes(query)
              );
            });
          }

          // If the bill doesn't match any search criteria, filter it out
          if (
            !(
              vendorName.includes(query) ||
              vendorFirm.includes(query) ||
              clientName.includes(query) ||
              billId.includes(query) ||
              poId.includes(query) ||
              category.includes(query) ||
              remarks.includes(query) ||
              status.includes(query) ||
              matchesInItems
            )
          ) {
            return false;
          }
        }

        // If the bill passes all filters, include it
        return true;
      })
      .sort((a, b) => {
        // Sort based on selected option
        switch (sortOption) {
          case "newest":
            // Sort by date (newest first)
            const getTimestamp = (dateStr) => {
              if (!dateStr) return 0;
              const [day, month, year] = dateStr.split("-").map(Number);
              if (!day || !month || !year) return 0;
              return new Date(year, month - 1, day).getTime();
            };
            return getTimestamp(b.date) - getTimestamp(a.date);

          case "oldest":
            // Sort by date (oldest first)
            const getTimestamp2 = (dateStr) => {
              if (!dateStr) return 0;
              const [day, month, year] = dateStr.split("-").map(Number);
              if (!day || !month || !year) return 0;
              return new Date(year, month - 1, day).getTime();
            };
            return getTimestamp2(a.date) - getTimestamp2(b.date);

          case "amountHigh":
            // Sort by amount (highest first)
            const amountA = parseFloat(a.totalAmount || 0);
            const amountB = parseFloat(b.totalAmount || 0);
            return amountB - amountA;

          case "amountLow":
            // Sort by amount (lowest first)
            const amountA2 = parseFloat(a.totalAmount || 0);
            const amountB2 = parseFloat(b.totalAmount || 0);
            return amountA2 - amountB2;

          case "status":
            // Sort by status
            const statusOrder = {
              Approved: 1,
              Declined: 2,
              Requested: 3,
              Paid: 4,
              Draft: 5,
            };
            return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);

          default:
            return 0;
        }
      });
  }, [
    bills,
    activeBillType,
    debouncedSearchQuery,
    selectedCategories,
    selectedFilters,
    sortOption,
    startDate,
    endDate,
  ]);

  // Highlight search matches in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;

    // Ensure text is a string
    const textStr = String(text);
    const lowerText = textStr.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();

    if (!lowerText.includes(lowerQuery)) return textStr;

    const index = lowerText.indexOf(lowerQuery);
    const before = textStr.substring(0, index);
    const match = textStr.substring(index, index + query.length);
    const after = textStr.substring(index + query.length);

    return (
      <>
        {before}
        <span className="highlight-text">{match}</span>
        {after}
      </>
    );
  };

  // Calculate bill raised amount for display
  const getBillRaisedAmount = (poEntry, relatedBills) => {
    if (!poEntry || !relatedBills || relatedBills.length === 0) {
      return 0;
    }

    // Sum total amount from all related bills
    return relatedBills.reduce((total, bill) => {
      return total + parseFloat(bill.totalAmount || 0);
    }, 0);
  };

  // View bill details function
  const viewBillDetails = (bill) => {
    try {
      // Find all bills related to this PO
      const allRelatedBills = bills.filter(
        (b) => !b.isPO && b.poId === bill.poId
      );

      // Find the PO entry for additional information
      const poEntry = bills.find((b) => b.isPO && b.poId === bill.poId);

      // Find index of current bill in allRelatedBills
      // We need to get the correct index considering bills may be in reverse order for display
      // To fix this, we'll use the actual timestamp/billId which should reflect chronological order

      // Sort the related bills by timestamp/billId in ascending order (oldest first)
      const chronologicalBills = [...allRelatedBills].sort((a, b) => {
        // Parse billId as timestamp if possible, otherwise just compare as strings
        const timeA = parseInt(a.billId);
        const timeB = parseInt(b.billId);

        // If both are valid timestamps, sort by time
        if (!isNaN(timeA) && !isNaN(timeB)) {
          return timeA - timeB;
        }

        // Otherwise sort by string comparison
        return String(a.billId).localeCompare(String(b.billId));
      });

      // Now find the index in this chronologically sorted array
      const billIndex = chronologicalBills.findIndex(
        (b) => b.billId === bill.billId
      );

      console.log("Bill index in chronological order:", billIndex);

      // Validate that we have all the necessary data
      if (!selectedProject || !bill || !poEntry) {
        toast.error("Missing required information to view bill");
        return;
      }

      // Navigate to edit bill page with data
      history.push("/RaiseBill", {
        data: {
          ProjectId: selectedProject.ProjectId,
          ProjectName: selectedProject.ClientName,
          viewMode: true, // Set view mode to true
          billId: bill.billId,
          billData: bill,
          poId: bill.poId,
          readOnly: bill.status === "Approved", // Make read-only if bill is approved
          relatedBills: chronologicalBills, // Pass the chronologically sorted bills
          currentBillIndex: billIndex >= 0 ? billIndex : 0,
          vendor: bill.vendor,
          client: bill.client,
          serviceOrder: poEntry || {}, // Pass PO data
        },
      });
    } catch (error) {
      console.error("Error viewing bill details:", error);
      toast.error("Error viewing bill details");
    }
  };

  // Handle bill approval or decline
  const handleBillApproval = async (bill, newStatus) => {
    try {
      setSpinner(true);
      setLoadingMessage(
        `${newStatus === "Approved" ? "Approving" : "Declining"} bill...`
      );

      // Get the bill data from Firebase
      const billResponse = await OrdersBillingAPI.GetBillingInformation(
        bill.poId
      );

      if (billResponse.isError) {
        throw new Error(
          billResponse.errorMessage || "Failed to retrieve bill data"
        );
      }

      // Find the specific bill by timestamp/billId
      if (!billResponse.data || !billResponse.data[bill.billId]) {
        throw new Error("Bill not found");
      }

      // Get the bill data to update
      const billData = billResponse.data[bill.billId];

      // Update the status and approval details
      billData.status = newStatus;
      billData.approvedBy = userContext.user?.DisplayName || "Admin";
      billData.approvedAt = `${billData.approvedBy} ${formatDate(
        new Date()
      )} ${new Date().toLocaleTimeString()}`;

      // Save the updated bill
      const updateResponse = await OrdersBillingAPI.UpdateBillingInformation(
        bill.poId,
        bill.billId,
        billData
      );

      if (updateResponse.isError) {
        throw new Error(
          updateResponse.errorMessage || "Failed to update bill status"
        );
      }

      // Show success message
      toast.success(`Bill ${newStatus.toLowerCase()} successfully`);

      // Refresh data to show updated status
      if (selectedProject) {
        fetchReleasedOrders(selectedProject);
      }
    } catch (error) {
      console.error(`Error ${newStatus.toLowerCase()} bill:`, error);
      toast.error(
        `Error ${newStatus.toLowerCase()} bill: ${
          error.message || "Unknown error"
        }`
      );
    } finally {
      setSpinner(false);
      setLoadingMessage("");
    }
  };

  return (
    <div className="billing-container">
      <div className="billing-content">
        <div className="billing-header">
          <div className="billing-title">
            <h1>Billing</h1>
            <div
              className="project-selector"
              ref={projectSelectorRef}
              onClick={(e) => e.stopPropagation()}
            >
              <DropDownMUI
                placeholder="Select Project"
                value={selectedProject?.ClientName || ""}
                data={projects.map((p) => p.ClientName)}
                onChange={(value) => {
                  const selectedProject = projects.find(
                    (p) => p.ClientName === value
                  );
                  if (selectedProject) {
                    handleProjectSelection(selectedProject.ProjectId);
                  }
                }}
                custStyles={{
                  width: 280,
                  height: 40,
                  marginTop: "5px",
                  fontSize: "0.7rem",
                }}
              />
            </div>
          </div>
          <div className="header-buttons">
            <button
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fdd34d",
                color: "black",
                border: "none",
                borderRadius: "6px",
                padding: "10px 20px",
                cursor: "pointer",
                fontSize: "0.8rem",
                marginRight: "10px",
                fontWeight: "bold",
                textTransform: "uppercase",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                transition: "background-color 0.3s ease",
              }}
              className="generate-po-btn"
              onClick={() => {
                history.push("/OrdersQuotations/" + selectedProject.ProjectId, {
                  data: {
                    ...project,
                    rooms: project.rooms,
                    ProjectId: project.ProjectId,
                    ClientName: project.ClientName,
                    Address: project.Address,
                    AmountRecieved: project.AmountReceived,
                    SiteEngineerId: project.SiteEngineersId,
                  },
                  selectedWorks: [],
                  selectedMaterials: [],
                  orderOrQuotation: "WorkOrder",
                });
              }}
            >
              Generate Work Order
            </button>
            {/* <button
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#fdd34d",
                color: "black",
                border: "none",
                borderRadius: "6px",
                padding: "10px 20px",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: "bold",
                textTransform: "uppercase",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                transition: "background-color 0.3s ease",
                marginRight: "10px",
              }}
              className="generate-nmr-btn"
              onClick={() => setShowNMRModal(true)}
            >
              Generate NMR Bill
            </button> */}
          </div>
        </div>

        {/* Add tabs */}
        {/* <div
          className="billing-tabs"
          role="tablist"
          aria-label="Billing options"
        >
          <button
            className={`tab ${activeTab === "regular" ? "active" : ""}`}
            onClick={() => setActiveTab("regular")}
            role="tab"
            aria-selected={activeTab === "regular"}
            aria-controls="regular-panel"
            id="regular-tab"
            type="button"
          >
            Regular Bills
          </button>
          <button
            className={`tab ${activeTab === "nmr" ? "active" : ""}`}
            onClick={() => setActiveTab("nmr")}
            role="tab"
            aria-selected={activeTab === "nmr"}
            aria-controls="nmr-panel"
            id="nmr-tab"
            type="button"
          >
            NMR Bills
          </button>
        </div> */}

        {/* Show content based on active tab */}
        {activeTab === "regular" ? (
          <>
            <div className="filter-bar">
              <div className="filter-group" ref={categoriesRef}>
                <button
                  className={`filter-btn ${
                    selectedCategories.length > 0 ? "active" : ""
                  }`}
                  onClick={toggleCategoriesDropdown}
                  disabled={isLoadingIncremental}
                >
                  Categories{" "}
                  {selectedCategories.length > 0 && (
                    <span className="filter-badge">
                      {selectedCategories.length}
                    </span>
                  )}{" "}
                  <FiFilter />
                </button>
                {categoriesDropdown && (
                  <div className="filter-dropdown">
                    <div className="dropdown-header">
                      <h4>Categories</h4>
                      <div>
                        {selectedCategories.length > 0 && (
                          <button
                            className="clear-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCategories([]);
                            }}
                          >
                            Clear
                          </button>
                        )}
                        <button
                          className="close-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategoriesDropdown(false);
                          }}
                        >
                          <FiX size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="dropdown-content">
                      {categories.map((category, index) => (
                        <div
                          key={index}
                          className={`dropdown-item ${
                            selectedCategories.includes(category)
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category);
                          }}
                        >
                          <div className="checkbox"></div>
                          {category}
                        </div>
                      ))}
                      {categories.length === 0 && (
                        <div className="dropdown-item">No categories found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-group" ref={sortRef}>
                <button
                  className={`filter-btn ${
                    sortOption !== "newest" ? "active" : ""
                  }`}
                  onClick={toggleSortDropdown}
                  disabled={isLoadingIncremental}
                >
                  Sort by{" "}
                  <span className="filter-tag">
                    {sortOption === "oldest" && "Oldest"}
                    {sortOption === "amountHigh" && "Amount ↓"}
                    {sortOption === "amountLow" && "Amount ↑"}
                    {sortOption === "status" && "Status"}
                    {sortOption === "newest" && "Latest"}
                  </span>{" "}
                  <BiSort />
                </button>
                {sortDropdown && (
                  <div className="filter-dropdown" style={{ display: "block" }}>
                    <div className="dropdown-header">
                      <h4>Sort by</h4>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        {sortOption !== "newest" && (
                          <button
                            className="clear-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSortOption("newest");
                            }}
                          >
                            Reset
                          </button>
                        )}
                        <button
                          className="close-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSortDropdown(false);
                          }}
                        >
                          <FiX size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="dropdown-content">
                      <div
                        className={`dropdown-item ${
                          sortOption === "newest" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSortSelection("newest");
                        }}
                      >
                        <div className="checkbox"></div>
                        Latest first
                      </div>
                      <div
                        className={`dropdown-item ${
                          sortOption === "oldest" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSortSelection("oldest");
                        }}
                      >
                        <div className="checkbox"></div>
                        Oldest first
                      </div>
                      <div
                        className={`dropdown-item ${
                          sortOption === "amountHigh" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSortSelection("amountHigh");
                        }}
                      >
                        <div className="checkbox"></div>
                        Amount (high to low)
                      </div>
                      <div
                        className={`dropdown-item ${
                          sortOption === "amountLow" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSortSelection("amountLow");
                        }}
                      >
                        <div className="checkbox"></div>
                        Amount (low to high)
                      </div>
                      <div
                        className={`dropdown-item ${
                          sortOption === "status" ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSortSelection("status");
                        }}
                      >
                        <div className="checkbox"></div>
                        Status
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="filter-group" ref={filtersRef}>
                <button
                  className={`filter-btn ${
                    selectedFilters.status.length > 0 ||
                    selectedFilters.hasBills !== null
                      ? "active"
                      : ""
                  }`}
                  onClick={toggleFiltersDropdown}
                  disabled={isLoadingIncremental}
                >
                  Filters{" "}
                  {(selectedFilters.status.length > 0 ||
                    selectedFilters.hasBills !== null) && (
                    <span className="filter-badge">
                      {selectedFilters.status.length +
                        (selectedFilters.hasBills !== null ? 1 : 0)}
                    </span>
                  )}{" "}
                  <FiSliders />
                </button>
                {filtersDropdown && (
                  <div className="filter-dropdown" style={{ display: "block" }}>
                    <div className="dropdown-header">
                      <h4>Filters</h4>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        {(selectedFilters.status.length > 0 ||
                          selectedFilters.hasBills !== null) && (
                          <button
                            className="clear-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFilters({
                                status: [],
                                hasBills: null,
                              });
                            }}
                          >
                            Clear All
                          </button>
                        )}
                        <button
                          className="close-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFiltersDropdown(false);
                          }}
                        >
                          <FiX size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="dropdown-content">
                      <h4
                        style={{
                          padding: "0 18px",
                          marginBottom: "10px",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        Status
                      </h4>
                      {[
                        "Approved",
                        "Declined",
                        "Requested",
                        "Paid",
                        "Draft",
                      ].map((status) => (
                        <div
                          key={status}
                          className={`dropdown-item ${
                            selectedFilters.status.includes(status)
                              ? "active"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter("status", status);
                          }}
                        >
                          <div className="checkbox"></div>
                          {status}
                        </div>
                      ))}
                      <h4
                        style={{
                          padding: "0 18px",
                          marginTop: "16px",
                          marginBottom: "10px",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        Bills
                      </h4>
                      <div
                        className={`dropdown-item ${
                          selectedFilters.hasBills === true ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilter("hasBills", true);
                        }}
                      >
                        <div className="checkbox"></div>
                        Has bills
                      </div>
                      <div
                        className={`dropdown-item ${
                          selectedFilters.hasBills === false ? "active" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFilter("hasBills", false);
                        }}
                      >
                        <div className="checkbox"></div>
                        No bills
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="date-filter">
                <div className="date-input">
                  <label>Start Date</label>
                  <div className="date-field" onClick={handleStartDateClick}>
                    <input
                      type="text"
                      value={startDate || ""}
                      readOnly
                      placeholder="DD-MM-YYYY"
                    />
                    <FiCalendar size={16} />
                  </div>
                </div>

                <div className="date-input">
                  <label>End Date</label>
                  <div className="date-field" onClick={handleEndDateClick}>
                    <input
                      type="text"
                      value={endDate || ""}
                      readOnly
                      placeholder="DD-MM-YYYY"
                    />
                    <FiCalendar size={16} />
                  </div>
                </div>

                {(startDate || endDate) && (
                  <button
                    className="clear-filter-btn"
                    onClick={clearDateFilters}
                    title="Clear date filters"
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>

              {/* Date pickers popover */}
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Popover
                  open={Boolean(startDateAnchor)}
                  anchorEl={startDateAnchor}
                  onClose={handleStartDateClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  container={document.body}
                >
                  <div className="date-picker-container">
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd-MM-yyyy"
                      margin="normal"
                      id="start-date-picker"
                      label="PO Start Date"
                      value={startDateObj}
                      onChange={handleStartDateChange}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      autoOk
                    />
                    <div className="date-picker-actions">
                      <button onClick={handleStartDateClose}>Cancel</button>
                      <button onClick={handleDateFilterApply}>Apply</button>
                    </div>
                  </div>
                </Popover>

                <Popover
                  open={Boolean(endDateAnchor)}
                  anchorEl={endDateAnchor}
                  onClose={handleEndDateClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  container={document.body}
                >
                  <div className="date-picker-container">
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd-MM-yyyy"
                      margin="normal"
                      id="end-date-picker"
                      label="PO End Date"
                      value={endDateObj}
                      onChange={handleEndDateChange}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      autoOk
                    />
                    <div className="date-picker-actions">
                      <button onClick={handleEndDateClose}>Cancel</button>
                      <button onClick={handleDateFilterApply}>Apply</button>
                    </div>
                  </div>
                </Popover>
              </MuiPickersUtilsProvider>

              <div className="search-box">
                <AiOutlineSearch />
                <input
                  type="text"
                  placeholder="Search here..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search bills"
                />
              </div>
            </div>

            {/* Bill type tabs */}
            <div className="bill-type-tabs">
              <div
                className={`bill-type-tab ${
                  activeBillType === "Material Bill" ? "active" : ""
                }`}
                onClick={() => handleBillTypeClick("Material Bill")}
              >
                Material Bill
              </div>
              <div
                className={`bill-type-tab ${
                  activeBillType === "Work Bill" ? "active" : ""
                }`}
                onClick={() => handleBillTypeClick("Work Bill")}
              >
                Work Bill
              </div>
              <div
                className={`bill-type-tab ${
                  activeBillType === "Work + Material" ? "active" : ""
                }`}
                onClick={() => handleBillTypeClick("Work + Material")}
              >
                Work + Material
              </div>
            </div>

            {/* Bills content area */}
            {!selectedProject ? (
              <div className="no-bills">
                <p>
                  No project selected. Please select a project to view its
                  bills.
                </p>
              </div>
            ) : loadedBills.length > 0 ? (
              // Show incrementally loaded bills while loading or when complete
              <div className="bills-list-container">
                <div className="bills-list">
                  {renderBillsList(
                    isLoadingIncremental ? loadedBills : filteredBills
                  )}
                </div>
              </div>
            ) : filteredBills.length === 0 && !isLoadingIncremental ? (
              <div className="no-bills">
                <p>
                  No bills found for project "{selectedProject.ClientName}".
                  Generate a new bill or select a different project.
                </p>
                <button
                  className="generate-btn"
                  disabled={loading}
                  onClick={() => {
                    history.push(
                      "/OrdersQuotations/" + selectedProject.ProjectId,
                      {
                        data: {
                          ...project,
                          rooms: project.rooms,
                          ProjectId: project.ProjectId,
                          ClientName: project.ClientName,
                          Address: project.Address,
                          AmountRecieved: project.AmountReceived,
                          SiteEngineerId: project.SiteEngineersId,
                        },
                        selectedWorks: [],
                        selectedMaterials: [],
                        orderOrQuotation: "WorkOrder",
                      }
                    );
                  }}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    "Generate Work Order"
                  )}
                </button>
              </div>
            ) : isLoadingIncremental && loadedBills.length === 0 ? (
              // Initial loading state - empty state with just a message
              <div className="no-bills loading-only">
                <p>
                  Loading data for project "{selectedProject.ClientName}"...
                </p>
              </div>
            ) : (
              // Normal display of filtered bills
              <div className="bills-list-container">
                <div className="bills-list">
                  {renderBillsList(filteredBills)}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="nmr-bills-content">
            <h2>NMR Bills</h2>
            <p>
              Select a project and click "Generate NMR Bill" to create a new NMR
              bill.
            </p>
          </div>
        )}

        {/* NMR Modal */}
        <NMRModal
          isOpen={showNMRModal}
          onClose={() => setShowNMRModal(false)}
          projects={projects}
          selectedProject={selectedProject}
        />

        {/* Progress indicator */}
        <ProgressIndicator
          isLoading={isLoadingIncremental}
          progress={loadingProgress}
          message={loadingMessage}
        />
      </div>
    </div>
  );

  // Helper function to render bills list with grouped POs
  function renderBillsList(billsToRender) {
    // Create a map of PO IDs to their bills
    const poMap = {};

    // First find all POs and group bills by PO
    billsToRender.forEach((bill) => {
      const poId = bill.poId;

      if (!poMap[poId]) {
        poMap[poId] = {
          po: null,
          bills: [],
          billsOriginalOrder: [], // Store bills in original order
        };
      }

      if (bill.isPO) {
        poMap[poId].po = bill;
      } else {
        poMap[poId].bills.push(bill);
        poMap[poId].billsOriginalOrder.push(bill);
      }
    });

    // Now render each PO with its associated bills
    return Object.keys(poMap).map((poId) => {
      const { po, billsOriginalOrder } = poMap[poId];

      // If we don't have the PO data, create a placeholder or skip
      if (!po) {
        return null;
      }

      // Calculate total bill amount for this PO and its bills
      const totalBillAmount = billsOriginalOrder.reduce((total, bill) => {
        return total + parseFloat(bill.totalAmount || 0);
      }, 0);

      // Return the rendering code for this PO and its bills
      return (
        <div key={poId} className={"bill-card"}>
          <div className="bill-card-header">
            <div
              className={`status-indicator ${getStatusColorClass(
                po.status || "Pending"
              )}`}
            ></div>
            <div className="bill-info">
              <div className="po-number">
                <span
                  className={
                    billsOriginalOrder.length > 0
                      ? "orange-circle-indicator"
                      : "red-circle-indicator"
                  }
                ></span>
                PO #:{" "}
                {debouncedSearchQuery
                  ? highlightText(po.poId.toString(), debouncedSearchQuery)
                  : po.poId}{" "}
                | {po.billType}{" "}
                <span className="po-status">
                  {po.isLoading ? (
                    <span className="loading-text-sm">Loading...</span>
                  ) : billsOriginalOrder.length > 0 ? (
                    ` | ${billsOriginalOrder.length} bill${
                      billsOriginalOrder.length > 1 ? "s" : ""
                    } generated`
                  ) : (
                    " | No bills generated yet"
                  )}
                </span>
              </div>
              <div className="bill-date">
                {formatDisplayDate(po.date) || "N/A"} | {po.time || "12:00 PM"}
              </div>
            </div>
            <div className="bill-actions">
              <button className="more-options">
                <FiMoreVertical size={20} />
              </button>
            </div>
          </div>

          <div className="bill-details">
            {po.isLoading ? (
              <div className="loading-items-container">
                <CircularProgress size={24} style={{ color: "#fdd34d" }} />
                <p>Loading purchase order details...</p>
              </div>
            ) : po.orderItems && po.orderItems.length > 0 ? (
              <div>
                <table className="bill-items-table po-items-table">
                  <thead className="table-header">
                    <tr>
                      <th>Line Items</th>
                      <th>PO Quantity</th>
                      <th>Billed Quantity</th>
                      <th>Unit</th>
                      <th>Rate</th>
                      <th>GST</th>
                      <th>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.orderItems.map((item, index) => (
                      <tr
                        key={index}
                        className={item.selected ? "selected-row" : ""}
                      >
                        <td>
                          <div
                            className="item-name"
                            onClick={() => handleItemSelection(po.poId, index)}
                          >
                            <div
                              className={`modern-checkbox ${
                                item.selected ? "selected" : ""
                              }`}
                              style={{ cursor: "pointer" }}
                              title={
                                item.selected ? "Deselect item" : "Select item"
                              }
                            >
                              {item.selected && <BiCheck size={16} />}
                            </div>
                            <div>
                              <div className="item-title">
                                {debouncedSearchQuery
                                  ? highlightText(
                                      item.heading ||
                                        po.vendor?.category ||
                                        "Civil",
                                      debouncedSearchQuery
                                    )
                                  : item.heading ||
                                    po.vendor?.category ||
                                    "Civil"}
                              </div>
                              <div className="item-desc">
                                {debouncedSearchQuery
                                  ? highlightText(
                                      item.specification ||
                                        item.description ||
                                        "Description",
                                      debouncedSearchQuery
                                    )
                                  : item.specification ||
                                    item.description ||
                                    "Description"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>{item.quantity || "0"}</td>
                        <td>
                          {billsOriginalOrder.length > 0
                            ? item.quantity || "0"
                            : "-"}
                        </td>
                        <td>{item.unit || "Lumpsum"}</td>
                        <td>₹ {item.rate || "0"}</td>
                        <td>{item.gst || "18"} %</td>

                        <td>₹ {formatAmount(calculateItemAmount(item))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-items">
                <p>No line items found for this purchase order.</p>
              </div>
            )}
          </div>

          <div className="bill-remarks">
            <span className="remarks-label">Remarks:</span>{" "}
            {debouncedSearchQuery
              ? highlightText(
                  po.remarks ||
                    "This is the previous remark updated by the other users",
                  debouncedSearchQuery
                )
              : po.remarks ||
                "This is the previous remark updated by the other users"}
            <button
              className="generate-bill-btn-inline"
              onClick={() => generateBill(po)}
              disabled={po.isLoading || generatingBillId === po.Id}
              title={
                po.isLoading
                  ? "Loading purchase order details..."
                  : "Generate new bill from this purchase order"
              }
            >
              {po.isLoading
                ? "Loading..."
                : generatingBillId === po.Id
                ? "Generating..."
                : "Generate Bill"}
            </button>
          </div>
          {/* Display related bills if they exist */}
          {billsOriginalOrder.length > 0 && (
            <div className="bill-details">
              <div>
                <table className="bill-items-table bill-details-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Category</th>
                      <th>Date</th>
                      <th>Vendor Name</th>
                      <th>Vendor Firm</th>
                      <th>Status</th>
                      <th>Bill Amount</th>
                      <th>Approved By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billsOriginalOrder.map((relatedBill, index) => (
                      <tr key={index}>
                        <td>
                          {/* Make bill number clickable with blue underlined styling */}
                          <a
                            className="bill-number-link"
                            onClick={() => viewBillDetails(relatedBill)}
                            title="Click to view bill details"
                          >
                            {debouncedSearchQuery
                              ? highlightText(
                                  relatedBill.billNo || relatedBill.billId,
                                  debouncedSearchQuery
                                )
                              : relatedBill.billNo || relatedBill.billId}
                          </a>
                        </td>
                        <td>
                          {debouncedSearchQuery
                            ? highlightText(
                                relatedBill.vendor?.category,
                                debouncedSearchQuery
                              )
                            : relatedBill.vendor?.category}
                        </td>
                        <td>{formatDisplayDate(relatedBill.date)}</td>
                        <td>
                          {debouncedSearchQuery
                            ? highlightText(
                                relatedBill.vendor?.name,
                                debouncedSearchQuery
                              )
                            : relatedBill.vendor?.name}
                        </td>
                        <td>
                          {debouncedSearchQuery
                            ? highlightText(
                                relatedBill.vendor?.firm,
                                debouncedSearchQuery
                              )
                            : relatedBill.vendor?.firm}
                        </td>
                        <td>
                          {relatedBill.status && (
                            <span
                              className={`status-pill status-${relatedBill.status.toLowerCase()}`}
                            >
                              {relatedBill.status}
                            </span>
                          )}
                        </td>
                        <td>₹ {formatAmount(relatedBill.totalAmount)} /- </td>
                        <td>{relatedBill.approvedBy}</td>
                        <td>
                          {/* Show approval buttons only for bills with "Requested" status */}
                          {relatedBill.status === "Requested" && (
                            <div className="bill-action-buttons">
                              <button
                                className="approve-btn"
                                onClick={() =>
                                  handleBillApproval(relatedBill, "Approved")
                                }
                                title="Approve bill"
                              >
                                <FiCheck size={16} />
                              </button>
                              <button
                                className="decline-btn"
                                onClick={() =>
                                  handleBillApproval(relatedBill, "Declined")
                                }
                                title="Decline bill"
                              >
                                <FiXCircle size={16} />
                              </button>
                            </div>
                          )}
                          {/* For other statuses, offer view option */}
                          {relatedBill.status !== "Requested" && (
                            <button
                              className="view-btn"
                              onClick={() => viewBillDetails(relatedBill)}
                              title="View bill details"
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    });
  }
};

export default drawerTemplate(Billing);
